/**
 * AppLayout - Layout component unificato per tutte le pagine autenticate.
 * Include: Topbar standardizzata, Sidebar responsive, MobileNav.
 *
 * Best practices:
 * - Layout pattern: un componente wrapper per mantenere consistenza
 * - Responsive: Sidebar hidden on mobile, MobileNav visible on mobile
 * - Props: configurabile per breadcrumb, actions custom, sidebar props
 */

import { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { PanelLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Bell, Settings, LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Breadcrumb, BreadcrumbItem } from "@/components/Breadcrumb";
import { Sidebar } from "@/components/Sidebar";
import { MobileNav } from "@/components/MobileNav";
import { authApi } from "@/api/auth";
import { useEmailStore } from "@/store/emailStore";
import { FilterType } from "@/store/emailStore";

interface AppLayoutProps {
  children: React.ReactNode;
  breadcrumbs: BreadcrumbItem[];
  /** Azioni custom nella topbar (right side, prima di avatar/logout) */
  topbarActions?: React.ReactNode;
  /** Props per Sidebar */
  sidebarProps?: {
    selectedFilter?: FilterType;
    onFilterChange?: (filter: FilterType) => void;
    counts?: {
      all: number;
      urgent: number;
      appointments: number;
      clinics: number;
      unassigned: number;
      conflicts: number;
    };
    showFilters?: boolean;
  };
  /** Nasconde sidebar (per pagine speciali) */
  hideSidebar?: boolean;
  /** Icona custom nel logo */
  logoIcon?: React.ReactNode;
  /** Titolo custom nel logo (default: VetMail) */
  logoTitle?: string;
  /** Handler per click su notification bell */
  onNotificationClick?: () => void;
  /** Home href per breadcrumb (default: /triage) */
  homeHref?: string;
  /** Padding del main content */
  mainClassName?: string;
}

export function AppLayout({
  children,
  breadcrumbs,
  topbarActions,
  sidebarProps,
  hideSidebar = false,
  logoIcon,
  logoTitle = "VetMail",
  onNotificationClick,
  homeHref = "/triage",
  mainClassName = "bg-background",
}: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const user = authApi.getUser();
  const { selectedVetId, setSelectedVet } = useEmailStore();

  const handleLogout = () => {
    navigate("/login", { state: { logout: true } });
  };

  const handleVetClick = (vetId: string | null) => {
    setSelectedVet(vetId);
    setShowSidebar(false);
    if (location.pathname !== "/" && location.pathname !== "/triage")
      navigate("/triage");
  };

  return (
    <div className="mail-app flex flex-col h-dvh bg-background">
      {/* Topbar - standard per tutte le pagine */}
      <header className="mail-app-header">
        <div className="flex items-center gap-3 min-w-0">
          {!hideSidebar && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Mostra caselle"
              title="Mostra caselle"
              className="md:hidden"
              onClick={() => setShowSidebar(true)}
            >
              <PanelLeft className="size-4" />
            </Button>
          )}
          <div className="flex items-center gap-2 shrink-0">
            <div className="p-1.5 bg-primary/10 rounded-lg">
              {logoIcon || <Mail className="size-4 text-primary" />}
            </div>
            <h1 className="text-lg font-semibold text-foreground">
              {logoTitle}
            </h1>
          </div>
          <div className="w-px h-5 bg-border" />
          <div className="min-w-0 truncate hidden sm:block">
            <Breadcrumb items={breadcrumbs} homeHref={homeHref} />
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Admin settings button - solo per admin */}
          {user?.role === "admin" && (
            <Link to="/admin">
              <Button variant="ghost" size="icon" title="Pannello Admin">
                <Settings className="size-4" />
              </Button>
            </Link>
          )}
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              onNotificationClick
                ? onNotificationClick()
                : window.dispatchEvent(new Event("vetmail-notifications"))
            }
            title="Notifiche"
            className="relative"
          >
            <Bell className="size-4" />
          </Button>
          <div className="w-px h-5 bg-border mx-1" />

          {/* User section - sempre presente */}
          <div className="flex items-center gap-2 ml-1">
            <Avatar className="size-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                {user?.full_name?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-foreground hidden xl:inline">
              {user?.full_name || "Utente"}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              title="Esci"
              className="ml-1"
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - hidden on mobile, visible on md+ */}
        {!hideSidebar && (
          <div className="hidden md:block">
            <Sidebar
              selectedFilter={sidebarProps?.selectedFilter}
              onFilterChange={sidebarProps?.onFilterChange}
              selectedVetId={selectedVetId}
              onVetClick={handleVetClick}
              counts={sidebarProps?.counts}
              showFilters={sidebarProps?.showFilters}
            />
          </div>
        )}

        {/* Main content */}
        <main
          className={`mail-content flex-1 min-w-0 overflow-y-auto pb-16 md:pb-0 ${mainClassName}`}
        >
          {topbarActions && (
            <div
              className="mail-page-toolbar"
              role="toolbar"
              aria-label="Azioni della pagina"
            >
              {topbarActions}
            </div>
          )}
          {children}
        </main>
      </div>

      {!hideSidebar && (
        <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
          <SheetContent side="left" className="p-0 w-[min(300px,90vw)]">
            <SheetTitle className="sr-only">Caselle e veterinari</SheetTitle>
            <div
              className="h-full pt-10"
              onClick={(event) => {
                if (
                  (event.target as HTMLElement).closest(
                    'a, button[title="Apri AI Chat"]',
                  )
                )
                  setShowSidebar(false);
              }}
            >
              <Sidebar
                selectedFilter={sidebarProps?.selectedFilter}
                onFilterChange={(filter) => {
                  sidebarProps?.onFilterChange?.(filter);
                  setShowSidebar(false);
                }}
                selectedVetId={selectedVetId}
                onVetClick={handleVetClick}
                counts={sidebarProps?.counts}
                showFilters={sidebarProps?.showFilters}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}
      {/* MobileNav - visible on mobile, hidden on md+ */}
      {!hideSidebar && <MobileNav />}
    </div>
  );
}
